import axios from 'axios';
import * as qs from 'qs';
import { ParamGetLocations, SearchType } from 'types/loctions';
import { Report, AdHocSession, MaintenanceIssues } from 'types/report';
import type { CriticalityDisplay } from 'utils/issueCriticality';

export type ParamGetModal = {
  size: number;
  page: number;
  keyword: string;
  orderBy: string;
  order: string;
  roomId: string;
  submittedAssetId?: number;
  searchType?: SearchType;
  archived?: boolean;
  status?: string;
};

export const getReportFile = async (params: Partial<ParamGetModal>) => {
  const query = qs.stringify({
    ...params
  });
  const { data } = await axios.get(`/reports/files?${query}`);
  return data;
};
export const getReport = async (params: Partial<ParamGetModal>) => {
  const query = qs.stringify({
    ...params
  });
  const { data } = await axios.get(`/reports?${query}`);
  return data;
};
export type PayloadCreateReport = {
  submittedAssetIds?: number[];
  storageIds?: number[];
  issueReportIds?: number[];
  roomLevelIssueIds?: number[];
  roomId?: number;
  submittedLocationIds?: number[];
  title: string;
  showAppendixFile?: boolean;
  selectAll?: boolean;
  onlyAsset?: boolean;
  date?: string;
  select?: string;
};
export const postReport = async (payload: PayloadCreateReport) => {
  const { data } = await axios.post('/reports', payload, {
    timeout: 280000,
    timeoutErrorMessage: 'Time out'
  });
  return data;
};

export const getReportDetail = async (id: number) => {
  const { data } = await axios.get(`/reports/${id}`);
  return data;
};

export const getFileReport = async ({
  roomId,
  params
}: {
  roomId: number;
  params: Partial<ParamGetModal>;
}) => {
  const query = qs.stringify({
    ...params
  });
  const { data } = await axios.get(`/locations/${roomId}/files?${query}`);
  return data;
};

export const deleteFileReport = async (id: number) => {
  const { data } = await axios.delete(`reports/files/${id}`);
  return data;
};
export type ParamsGetListRoomLevelIssues = ParamGetLocations & {
  archived?: boolean;
  sortBy?: string;
  searchType?: SearchType;
};
export const getListRoomLevelIssues = async ({
  params
}: {
  params: Partial<ParamsGetListRoomLevelIssues>;
}) => {
  const query = qs.stringify(
    {
      ...params
    },
    { arrayFormat: 'repeat', skipNulls: true }
  );
  const { data } = await axios.get(`/reports/room-issues?${query}`);
  return data;
};

export const sendReport = async (id: number, payload: { emails: string[] }) => {
  const { data } = await axios.post(`/reports/files/${id}/send-email`, payload);
  return data;
};

export type ParamsGetAssetIssueReports = ParamGetModal & {
  status?: string;
  clientId?: number;
  locationId?: number;
  assignedToUserId?: number;
  criticalityKey?: string;
};

export type AssetIssueReportResponse = {
  success: boolean;
  message: string;
  total: number;
  reports: Report[];
};

export const getAssetIssuesReport = async (
  params: Partial<ParamsGetAssetIssueReports>
): Promise<AssetIssueReportResponse> => {
  const query = qs.stringify(
    {
      ...params
    },
    { arrayFormat: 'repeat', skipNulls: true }
  );
  const { data } = await axios.get(`/reports?${query}`);
  return data;
};

export type ParamsGetMaintenanceIssue = ParamGetModal & {
  status?: string;
  clientId?: number;
  locationId?: number;
  assignedToUserId?: number;
  criticalityKey?: string;
};
export type MaintenanceIssueResponse = {
  success: boolean;
  total: number;
  issues: MaintenanceIssues[];
};

export const getMaintenanceIssues = async (
  params: Partial<ParamsGetMaintenanceIssue>
): Promise<MaintenanceIssueResponse> => {
  const query = qs.stringify(
    {
      ...params
    },
    { arrayFormat: 'repeat', skipNulls: true }
  );
  const { data } = await axios.get(`/maintenance-issues?${query}`);
  return data;
};

export type AdHocIssueMinimal = {
  id: number;
  name?: string;
  locationPathString?: string;
  status?: string;
  createdAt?: string;
  description?: string;
  reportedByUser?: {
    id: number;
    name?: string;
    email?: string;
  } | null;
  locationLevelIds?: {
    l1Id?: number;
    l2Id?: number;
    l3Id?: number;
    l4Id?: number;
    l5Id?: number;
  };
};

export type IssueUserRef = {
  id: number;
  name: string;
  email?: string;
} | null;

export type WorkflowIssue = {
  id: number;
  submitId?: number;
  submittedAssetId?: number;
  name?: string;
  description?: string;
  status?: string;
  dueDate?: string | null;
  note?: string | null;
  criticalityKey?: string | null;
  criticality?: CriticalityDisplay;
  assignedByUser?: IssueUserRef;
  assignedToUser?: IssueUserRef;
  reportedByUser?: IssueUserRef;
  sessions?: AdHocSession[];
  actionBy?: string;
  actionByDate?: string;
  resolutionDate?: string | null;
  type?: string;
  equipmentId?: string;
  complianceIssue?: string;
  actionNeeded?: string;
  clientId?: number;
  room?: Report['room'];
  submitted?: {
    id?: number;
    clientId?: number;
    roomId?: number;
    client?: { id?: number; clientName?: string };
    room?: Report['room'];
  };
  submittedAsset?: {
    id?: number;
    submitId?: number;
    submitted?: {
      id?: number;
      clientId?: number;
      roomId?: number;
      client?: { id?: number; clientName?: string };
    };
  };
  options?: { type: string; value: string; fieldType?: string }[];
  roomLevelIssuePhotos?: { id: number; url?: string; photo?: { url?: string } }[];
  issueReportPhotos?: {
    id: number;
    url?: string;
    photoType?: string;
    photo?: { url?: string; fileName?: string };
  }[];
  issuePhotosBefore?: { id: number; url?: string; photo?: { url?: string; fileName?: string } }[];
  issuePhotosAfter?: { id: number; url?: string; photo?: { url?: string; fileName?: string } }[];
};

/** Resolve route ids for asset issue APIs from list/detail shapes. */
export const resolveAssetIssueIds = (issue: {
  id?: number;
  submitId?: number;
  submittedAssetId?: number;
  submittedAsset?: { id?: number; submitId?: number };
}): { issueId: number; submitId?: number; submittedAssetId?: number } | null => {
  const issueId = issue.id != null ? Number(issue.id) : NaN;
  if (!Number.isFinite(issueId)) return null;
  const submittedAssetId =
    issue.submittedAssetId != null
      ? Number(issue.submittedAssetId)
      : issue.submittedAsset?.id != null
        ? Number(issue.submittedAsset.id)
        : undefined;
  const submitId =
    issue.submitId != null
      ? Number(issue.submitId)
      : issue.submittedAsset?.submitId != null
        ? Number(issue.submittedAsset.submitId)
        : undefined;
  return { issueId, submitId, submittedAssetId };
};

/** Resolve route ids for room issue APIs from list/detail shapes. */
export const resolveRoomIssueIds = (issue: {
  id?: number;
  submitId?: number;
}): { issueId: number; submitId?: number } | null => {
  const issueId = issue.id != null ? Number(issue.id) : NaN;
  if (!Number.isFinite(issueId)) return null;
  const submitId = issue.submitId != null ? Number(issue.submitId) : undefined;
  return { issueId, submitId };
};

export type AdHocIssue = {
  id: number;
  clientId?: number;
  locationId?: number;
  criticalityKey?: string | null;
  criticality?: CriticalityDisplay;
  locationPathString: string;
  name?: string;
  locationLevelIds?: {
    l1Id?: number;
    l2Id?: number;
    l3Id?: number;
    l4Id?: number;
    l5Id?: number;
  };
  locationPathIds?: number[];
  description: string;
  locationDescription: string;
  status: string;
  dueDate?: string | null;
  resolvedAt?: string | null;
  assignedByUserId?: number | null;
  assignedToUserId?: number | null;
  lat?: string | null;
  long?: string | null;
  altitude?: string | null;
  createdAt: string;
  updatedAt: string;
  possibleDuplicateOfId?: number | null;
  possibleDuplicateOf?: AdHocIssueMinimal | null;
  isPotentialDuplicate?: boolean;
  duplicateIssues?: AdHocIssueMinimal[];
  client?: {
    id: number;
    clientName: string;
  } | null;
  location?: {
    id: number;
    name: string;
  } | null;
  reportedByUser?: {
    id: number;
    name: string;
    email: string;
  } | null;
  assignedByUser?: {
    id: number;
    name: string;
    email: string;
  } | null;
  assignedToUser?: {
    id: number;
    name: string;
    email: string;
  } | null;
  locationPhotos?: {
    id: number;
    photoId: number;
    photoType?: string | null;
    uploadedByUserId?: number | null;
    photo?: {
      id: number;
      fileName?: string;
      type?: string;
      url?: string;
    } | null;
  }[];
  issuePhotosBefore?: {
    id: number;
    photoId: number;
    photoType?: string | null;
    uploadedByUserId?: number | null;
    photo?: {
      id: number;
      fileName?: string;
      type?: string;
      url?: string;
    } | null;
  }[];
  issuePhotosAfter?: {
    id: number;
    photoId: number;
    photoType?: string | null;
    uploadedByUserId?: number | null;
    photo?: {
      id: number;
      fileName?: string;
      type?: string;
      url?: string;
    } | null;
  }[];
  sessions?: unknown[];
};

export type ParamsGetAdHocIssues = {
  size?: number;
  page?: number;
  keyword?: string;
  clientId?: number;
  locationId?: number;
  assignedToUserId?: number;
  reportedByUserId?: number;
  status?: string;
  criticalityKey?: string;
  archived?: boolean;
  includeDuplicates?: boolean;
  order?: string;
  orderBy?: string;
};

export type AdHocIssuesResponse = {
  success: boolean;
  total: number;
  issues: AdHocIssue[];
};

export const getAdHocIssues = async (
  params: Partial<ParamsGetAdHocIssues>
): Promise<AdHocIssuesResponse> => {
  const query = qs.stringify(
    {
      ...params
    },
    { arrayFormat: 'repeat', skipNulls: true }
  );
  const { data } = await axios.get(`/issues/ad-hoc?${query}`);
  return data;
};

export type AssignmentRow = {
  id: string;
  issueType: 'Room' | 'Asset' | 'AdHoc' | 'Maintenance';
  clientName: string;
  clientId?: number;
  location: string;
  locationId?: number;
  locationPathString?: string;
  locationLevelIds?: {
    l1Id?: number;
    l2Id?: number;
    l3Id?: number;
    l4Id?: number;
    l5Id?: number;
  };
  locationPathIds?: number[];
  description: string;
  assignedTo: string;
  assignedBy?: string;
  assignedToUserId?: number;
  assignedByUserId?: number;
  actionByDate?: string;
  dueDate?: string;
  criticalityKey?: string | null;
  criticalityLabel?: string;
  criticalityColor?: string;
  status: string;
  link?: string;
  submitId?: number;
  issueId?: number;
  submittedAssetId?: number;
};

export type ParamsGetAssignments = {
  page?: number;
  size?: number;
  issueType?: 'Room' | 'Asset' | 'AdHoc' | 'Maintenance';
  clientName?: string;
  locationId?: number;
  status?: string;
  assignedToUserId?: number;
  criticalityKey?: string;
  orderBy?: string;
  order?: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
};

export type GetAssignmentsResponse = {
  success: boolean;
  total: number;
  assignments: AssignmentRow[];
};

export const getAssignments = async (
  params: Partial<ParamsGetAssignments>
): Promise<GetAssignmentsResponse> => {
  const query = qs.stringify({ ...params }, { arrayFormat: 'repeat', skipNulls: true });
  const { data } = await axios.get(`/issues/assignments?${query}`);
  return data;
};

export const getAdHocIssueDetail = async (
  id: number
): Promise<{ success: boolean; issue: AdHocIssue }> => {
  const { data } = await axios.get(`/issues/ad-hoc/${id}`);
  return data;
};

export type PayloadUpdateAdHocIssue = {
  name?: string;
  assignedToUserId?: number;
  assignedByUserId?: number;
  dueDate?: string;
  criticalityKey?: string | null;
  status?: string;
  resolvedAt?: string;
  description?: string;
  locationDescription?: string;
  altitude?: string;
  locationId?: number | null;
  possibleDuplicateOfId?: number | null;
  deletedLocationPhotoIds?: number[];
  deletedIssueBeforePhotoIds?: number[];
  deletedIssueAfterPhotoIds?: number[];
  locationPhotos?: File[];
  issuePhotosBefore?: File[];
  issuePhotosAfter?: File[];
};

export const updateAdHocIssue = async (
  id: number,
  payload: PayloadUpdateAdHocIssue
): Promise<{ success: boolean; issue: AdHocIssue }> => {
  const hasFiles =
    (payload.locationPhotos && payload.locationPhotos.length > 0) ||
    (payload.issuePhotosBefore && payload.issuePhotosBefore.length > 0) ||
    (payload.issuePhotosAfter && payload.issuePhotosAfter.length > 0);

  const deletedLocationPhotoIds =
    payload.deletedLocationPhotoIds && payload.deletedLocationPhotoIds.length
      ? JSON.stringify(payload.deletedLocationPhotoIds)
      : undefined;
  const deletedIssueBeforePhotoIds =
    payload.deletedIssueBeforePhotoIds && payload.deletedIssueBeforePhotoIds.length
      ? JSON.stringify(payload.deletedIssueBeforePhotoIds)
      : undefined;
  const deletedIssueAfterPhotoIds =
    payload.deletedIssueAfterPhotoIds && payload.deletedIssueAfterPhotoIds.length
      ? JSON.stringify(payload.deletedIssueAfterPhotoIds)
      : undefined;

  if (!hasFiles) {
    const { data } = await axios.put(`/issues/ad-hoc/${id}`, {
      ...payload,
      criticalityKey: payload.criticalityKey,
      deletedLocationPhotoIds,
      deletedIssueBeforePhotoIds,
      deletedIssueAfterPhotoIds
    });
    return data;
  }

  const formData = new FormData();
  if (payload.assignedToUserId !== undefined) {
    formData.append('assignedToUserId', String(payload.assignedToUserId));
  }
  if (payload.assignedByUserId !== undefined) {
    formData.append('assignedByUserId', String(payload.assignedByUserId));
  }
  if (payload.dueDate) formData.append('dueDate', payload.dueDate);
  if (payload.criticalityKey !== undefined) {
    formData.append(
      'criticalityKey',
      payload.criticalityKey === null ? '' : payload.criticalityKey
    );
  }
  if (payload.status) formData.append('status', payload.status);
  if (payload.resolvedAt) formData.append('resolvedAt', payload.resolvedAt);
  if (payload.name) formData.append('name', payload.name);
  if (payload.description) formData.append('description', payload.description);
  if (payload.locationDescription) {
    formData.append('locationDescription', payload.locationDescription);
  }
  if (payload.locationId !== undefined) {
    formData.append('locationId', payload.locationId === null ? '' : String(payload.locationId));
  }
  if (payload.possibleDuplicateOfId !== undefined) {
    formData.append(
      'possibleDuplicateOfId',
      payload.possibleDuplicateOfId === null ? '' : String(payload.possibleDuplicateOfId)
    );
  }

  if (deletedLocationPhotoIds) formData.append('deletedLocationPhotoIds', deletedLocationPhotoIds);
  if (deletedIssueBeforePhotoIds)
    formData.append('deletedIssueBeforePhotoIds', deletedIssueBeforePhotoIds);
  if (deletedIssueAfterPhotoIds)
    formData.append('deletedIssueAfterPhotoIds', deletedIssueAfterPhotoIds);

  if (payload.locationPhotos?.length) {
    payload.locationPhotos.forEach((file) => {
      formData.append('files', file);
      formData.append('photoTypes', 'LOCATION');
    });
  }
  if (payload.issuePhotosBefore?.length) {
    payload.issuePhotosBefore.forEach((file) => {
      formData.append('files', file);
      formData.append('photoTypes', 'ISSUE_BEFORE');
    });
  }
  if (payload.issuePhotosAfter?.length) {
    payload.issuePhotosAfter.forEach((file) => {
      formData.append('files', file);
      formData.append('photoTypes', 'ISSUE_AFTER');
    });
  }

  const { data } = await axios.put(`/issues/ad-hoc/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return data;
};

export type PayloadCreateAdHocIssue = {
  clientId: number;
  locationId: number;
  locationPathString: string;
  name?: string;
  description: string;
  locationDescription: string;
  assignedToUserId?: number;
  dueDate?: string;
  status?: string;
  locationPhotos?: File[];
  issuePhotosBefore?: File[];
  issuePhotosAfter?: File[];
};

export const createAdHocIssue = async (
  payload: PayloadCreateAdHocIssue
): Promise<{ success: boolean; issue: AdHocIssue }> => {
  const formData = new FormData();
  formData.append('clientId', String(payload.clientId));
  formData.append('locationId', String(payload.locationId));
  formData.append('locationPathString', payload.locationPathString);
  if (payload.name) formData.append('name', payload.name);
  formData.append('description', payload.description);
  formData.append('locationDescription', payload.locationDescription);
  if (payload.assignedToUserId) {
    formData.append('assignedToUserId', String(payload.assignedToUserId));
  }
  if (payload.dueDate) {
    formData.append('dueDate', payload.dueDate);
  }
  if (payload.status) {
    formData.append('status', payload.status);
  }

  if (payload.locationPhotos && payload.locationPhotos.length > 0) {
    payload.locationPhotos.forEach((file) => {
      formData.append('files', file);
      formData.append('photoTypes', 'LOCATION');
    });
  }

  if (payload.issuePhotosBefore && payload.issuePhotosBefore.length > 0) {
    payload.issuePhotosBefore.forEach((file) => {
      formData.append('files', file);
      formData.append('photoTypes', 'ISSUE_BEFORE');
    });
  }

  if (payload.issuePhotosAfter && payload.issuePhotosAfter.length > 0) {
    payload.issuePhotosAfter.forEach((file) => {
      formData.append('files', file);
      formData.append('photoTypes', 'ISSUE_AFTER');
    });
  }

  const { data } = await axios.post('/issues/ad-hoc', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return data;
};

export type PayloadUpdateAdHocSession = {
  date?: string;
  timeIn?: string;
  timeOut?: string | null;
  materialsUsed?: string;
  equipmentUsed?: string;
  technicianUserIds?: number[];
};

export const updateAdHocSession = async (
  issueId: number,
  sessionId: number,
  payload: PayloadUpdateAdHocSession
): Promise<{ success: boolean; session: unknown }> => {
  const { data } = await axios.put(`/issues/ad-hoc/${issueId}/sessions/${sessionId}`, payload);
  return data;
};

export type PayloadIssueAssignment = {
  assignedToUserId?: number | null;
  dueDate?: string | null;
  criticalityKey?: string | null;
  status?: string;
};

export type PayloadIssueNotify = {
  userIds: number[];
  channels: string[];
  message?: string;
};

export const getRoomIssueDetail = async (
  submitId: number,
  issueId: number
): Promise<{ success: boolean; roomIssue: WorkflowIssue }> => {
  const { data } = await axios.get(`/inspections/locations/${submitId}/room-issues/${issueId}`);
  return data;
};

export const loadRoomIssueById = async (
  issueId: number,
  submitId?: number
): Promise<{ success: boolean; roomIssue: WorkflowIssue }> => {
  // GET route requires submitId in path but does not validate it; use hint or placeholder for load only.
  const routeSubmitId = submitId != null && Number.isFinite(submitId) ? submitId : 1;
  const data = await getRoomIssueDetail(routeSubmitId, issueId);
  return data;
};

export const patchRoomIssueAssignment = async (
  submitId: number,
  issueId: number,
  payload: PayloadIssueAssignment
): Promise<{ success: boolean; roomIssue: WorkflowIssue }> => {
  const { data } = await axios.patch(
    `/inspections/locations/${submitId}/room-issues/${issueId}/assignment`,
    payload
  );
  return data;
};

export const createRoomIssueSession = async (
  submitId: number,
  issueId: number,
  payload: PayloadUpdateAdHocSession
): Promise<{ success: boolean; session: AdHocSession }> => {
  const { data } = await axios.post(
    `/inspections/locations/${submitId}/room-issues/${issueId}/sessions`,
    payload
  );
  return data;
};

export const updateRoomIssueSession = async (
  submitId: number,
  issueId: number,
  sessionId: number,
  payload: PayloadUpdateAdHocSession
): Promise<{ success: boolean; session: AdHocSession }> => {
  const { data } = await axios.put(
    `/inspections/locations/${submitId}/room-issues/${issueId}/sessions/${sessionId}`,
    payload
  );
  return data;
};

export const notifyRoomIssue = async (
  submitId: number,
  issueId: number,
  payload: PayloadIssueNotify
): Promise<{ success: boolean; message?: string }> => {
  const { data } = await axios.post(
    `/inspections/locations/${submitId}/room-issues/${issueId}/notify`,
    payload
  );
  return data;
};

export const getAssetIssueDetail = async (
  submitId: number,
  submittedAssetId: number,
  issueId: number
): Promise<{ success: boolean; issue: WorkflowIssue }> => {
  const detail = await getReportDetail(issueId);
  const issue = detail?.result as WorkflowIssue;
  if (!issue) {
    throw new Error('Asset issue not found');
  }
  return {
    success: true,
    issue: {
      ...issue,
      submitId: submitId || issue.submitId || issue.submittedAsset?.submitId,
      submittedAssetId: submittedAssetId || issue.submittedAssetId || issue.submittedAsset?.id
    }
  };
};

export const loadAssetIssueById = async (
  issueId: number
): Promise<{ success: boolean; issue: WorkflowIssue }> => {
  const detail = await getReportDetail(issueId);
  const issue = detail?.result as WorkflowIssue;
  if (!issue) {
    throw new Error('Asset issue not found');
  }
  return { success: true, issue };
};

export const patchAssetIssueAssignment = async (
  submitId: number,
  submittedAssetId: number,
  issueId: number,
  payload: PayloadIssueAssignment
): Promise<{ success: boolean; issue: WorkflowIssue }> => {
  const { data } = await axios.patch(
    `/inspections/locations/${submitId}/submitted-assets/${submittedAssetId}/issues/${issueId}/assignment`,
    payload
  );
  return data;
};

export const createAssetIssueSession = async (
  submitId: number,
  submittedAssetId: number,
  issueId: number,
  payload: PayloadUpdateAdHocSession
): Promise<{ success: boolean; session: AdHocSession }> => {
  const { data } = await axios.post(
    `/inspections/locations/${submitId}/submitted-assets/${submittedAssetId}/issues/${issueId}/sessions`,
    payload
  );
  return data;
};

export const updateAssetIssueSession = async (
  submitId: number,
  submittedAssetId: number,
  issueId: number,
  sessionId: number,
  payload: PayloadUpdateAdHocSession
): Promise<{ success: boolean; session: AdHocSession }> => {
  const { data } = await axios.put(
    `/inspections/locations/${submitId}/submitted-assets/${submittedAssetId}/issues/${issueId}/sessions/${sessionId}`,
    payload
  );
  return data;
};

export type MaintenanceIssueUserRef = { id: string; name: string; email: string };

export type MaintenanceIssuePhoto = {
  id: string;
  maintenanceIssueId: string;
  storageId: string;
  photoType: 'LOCATION' | 'ISSUE_BEFORE' | 'ISSUE_AFTER';
  url: string;
};

export type MaintenanceIssue = {
  id: string;
  assetId: string;
  assetName: string | null;
  assetDeviceId: string;
  assetDeviceName: string | null;
  location: string | null;
  assetClientName: string | null;
  status: 'ACTIVE' | 'RESOLVED';
  description: string | null;
  notes: string | null;
  criticalityKey: string | null;
  assignedBy: MaintenanceIssueUserRef | null;
  assignedTo: MaintenanceIssueUserRef | null;
  dueDate: string | null;
  resolutionDate?: string | null;
  createdAt: string;
  photos: MaintenanceIssuePhoto[];
};

export const loadMaintenanceIssueById = async (
  issueId: number
): Promise<{ success: boolean; issue: MaintenanceIssue }> => {
  const { data } = await axios.get(`/maintenance-issues/${issueId}`);
  return data;
};

export type PayloadUpdateMaintenanceIssue = {
  assignedToUserId?: number | string;
  dueDate?: string;
  criticalityKey?: string;
  status?: string;
  notes?: string | null;
  deletePhotoIds?: number[];
  files?: File[];
  photoTypes?: string[];
};

export const updateMaintenanceIssue = async (
  issueId: number,
  payload: PayloadUpdateMaintenanceIssue
): Promise<{ success: boolean; issue: MaintenanceIssue }> => {
  const formData = new FormData();
  if (payload.assignedToUserId !== undefined) {
    formData.append('assignedToUserId', String(payload.assignedToUserId));
  }
  if (payload.dueDate) formData.append('dueDate', payload.dueDate);
  if (payload.criticalityKey !== undefined) {
    formData.append('criticalityKey', payload.criticalityKey);
  }
  if (payload.status) formData.append('status', payload.status);
  if (payload.notes !== undefined) formData.append('notes', payload.notes || '');
  if (payload.deletePhotoIds?.length) {
    formData.append('deletePhotoIds', JSON.stringify(payload.deletePhotoIds));
  }
  if (payload.files?.length) {
    payload.files.forEach((file, index) => {
      formData.append('files', file);
      if (payload.photoTypes?.[index]) formData.append('photoTypes', payload.photoTypes[index]);
    });
  }
  const { data } = await axios.put(`/maintenance-issues/${issueId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return data;
};

export const notifyAssetIssue = async (
  submitId: number,
  submittedAssetId: number,
  issueId: number,
  payload: PayloadIssueNotify
): Promise<{ success: boolean; message?: string }> => {
  const { data } = await axios.post(
    `/inspections/locations/${submitId}/submitted-assets/${submittedAssetId}/issues/${issueId}/notify`,
    payload
  );
  return data;
};

export const notifyAdHocIssue = async (
  issueId: number,
  payload: PayloadIssueNotify
): Promise<{ success: boolean; message?: string }> => {
  const { data } = await axios.post(`/issues/ad-hoc/${issueId}/notify`, payload);
  return data;
};
